import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * StartTestNudge
 * - Bouncy entry (no hover effects), subtle float.
 * - Background graduation-hat "confetti" drifting behind the card.
 *
 * Props:
 *  to (string)                → route to test page (default "/test")
 *  initialDelayMs (number)    → delay before showing (default 450)
 *  rememberDismiss (boolean)  → store dismissal (default true)
 *  storageKey (string)        → localStorage key (default "stn-dismissed-v2")
 *  englishTitle / englishBody / arabicTitle / arabicBody / ctaLabel (optional)
 */
const StartTestNudge = ({
  to = '/test',
  initialDelayMs = 450,
  rememberDismiss = true,
  storageKey = 'stn-dismissed-v2',
  englishTitle = '🌟 Unsure which major suits you best?',
  englishBody = 'Discover your path with our expert-designed assessment — a life-changing insight, not just a test.\n\n🎓 Your future deserves this investment.',
  arabicTitle = '🌟 محتار أي تخصص يناسبك؟',
  arabicBody = 'اكتشف مسارك المهني مع تقييمنا المُصمّم من قِبل خبرائنا - رؤية تُغيّر حياتك، وليس مجرد اختبار.\n\n🎓 مستقبلك يستحق هذا الاستثمار.',
  ctaLabel,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';
  const align = isAr ? 'right' : 'left';

  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    rememberDismiss && typeof window !== 'undefined'
      ? localStorage.getItem(storageKey) === '1'
      : false
  );

  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => setVisible(true), initialDelayMs);
    return () => clearTimeout(t);
  }, [dismissed, initialDelayMs]);

  const onClose = () => {
    setVisible(false);
  };
  const onStart = () => navigate(to);

  // Confetti hats (emoji or SVG). We’ll render a few with randomized paths.
  const HATS_COUNT = 10;
  const hats = useMemo(() => {
    return Array.from({ length: HATS_COUNT }).map((_, i) => {
      const size = Math.floor(Math.random() * 14) + 12; // 12–26px
      const left = Math.random() * 100; // percentage
      const duration = Math.random() * 6 + 2; // 6–8s
      const delay = Math.random() * 0; // 0–3s
      const rotate = Math.random() * 40 - 20; // -20..20
      const drift = (Math.random() * 20 - 10).toFixed(1); // -10..10px
      return { id: i, size, left, duration, delay, rotate, drift };
    });
  }, []);

  const bodyLines = useMemo(() => {
    const text = isAr ? arabicBody : englishBody;
    return text.split('\n');
  }, [isAr, englishBody, arabicBody]);

  if (dismissed) return null;

  return (
    <>
      <style>{`
        /* Keyframes */
        @keyframes stn-bounce-in {
          0%   { transform: translate(-12px, 20px) scale(0.92); opacity: 0; }
          60%  { transform: translate(0, 0) scale(1.04); opacity: 1; }
          80%  { transform: translate(0, 0) scale(0.995); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes stn-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes stn-sheen {
          0% { transform: translateX(-120%); }
          35% { transform: translateX(120%); }
          100% { transform: translateX(120%); }
        }
        /* Confetti drift upwards with a tiny sway */
        @keyframes stn-confetti {
          0%   { transform: translateY(40px) translateX(0) rotate(0deg); opacity: 0; }
          10%  { opacity: .35; }
          50%  { transform: translateY(-60vh) translateX(var(--drift)) rotate(var(--rot)); opacity: .8; }
          100% { transform: translateY(-100vh) translateX(calc(var(--drift) * 1.5)) rotate(calc(var(--rot) * 1.8)); opacity: 0; }
        }

        /* Wrapper placement */
        .stn-wrap {
          position: fixed;
          left: 16px;
          bottom: 16px;
          z-index: 10010;
          pointer-events: none; /* card handles clicks */
        }
        @media (max-width: 540px) {
          .stn-wrap { left: 12px; right: 12px; bottom: 12px; }
        }

        /* Confetti layer behind the card */
        .stn-confetti-layer {
          position: absolute;
          inset: -200px -20px 0 -20px;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .stn-hat {
          position: absolute;
          bottom: 0;
          filter: drop-shadow(0 1px 4px rgba(0,0,0,.15));
          opacity: .75;
          animation-name: stn-confetti;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }

        /* Card */
        .stn-card {
          pointer-events: auto;
          position: relative;
          max-width: 500px;
          border-radius: 18px;
          padding: 16px;
          background: rgba(255,255,255,0.82);
          -webkit-backdrop-filter: blur(12px);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(2,6,23,0.08);
          box-shadow: 0 18px 40px rgba(2,6,23,.18);
          overflow: clip;
          isolation: isolate;

          /* gradient ring glow (soft) */
          --ring: linear-gradient(100deg, #7c3aed, #4f46e5, #06b6d4, #22c55e, #eab308, #7c3aed);
          position: relative;
          z-index: 1;

          animation: stn-bounce-in 520ms cubic-bezier(.2,.8,.2,1) forwards,
                     stn-float 5.5s ease-in-out 800ms infinite;
        }
        .stn-card::before {
          content: "";
          position: absolute; inset: -6px;
          border-radius: 24px;
          background: var(--ring);
          background-size: 300% 100%;
          filter: blur(18px);
          opacity: .5;
          z-index: -1;
          animation: border-pan 9s linear infinite;
        }
        @keyframes border-pan { 
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }

        .stn-grid {
          display: grid; grid-template-columns: 56px 1fr;
          gap: 12px; align-items: start;
        }
        .stn-icon {
          width: 56px; height: 56px; border-radius: 14px;
          display: grid; place-items: center;
          background: radial-gradient(120% 120% at 20% 15%, #fff 0%, #f5f3ff 40%, #eef2ff 75%, #e0f2fe 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.7);
        }
        .stn-cap { width: 26px; height: 26px; }

        .stn-title {
          margin: 2px 0 6px 0;
          font-weight: 800;
          font-size: 18px;
          color: #0b1220;
          letter-spacing: .2px;
        }
        .stn-text {
          margin: 0 0 14px 0;
          font-size: 15px;
          color: #27324a;
          line-height: 1.55;
        }

        .stn-actions {
          display: flex; gap: 10px; align-items: center; justify-content: space-around; padding: 0px 40px
        }
        .stn-cta :where(button) {
          position: relative; overflow: hidden; border-radius: 999px;
        }
        .stn-cta :where(button)::after {
          content: "";
          position: absolute; inset: 0;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,.38) 40%, transparent 70%);
          transform: translateX(-120%);
          animation: stn-sheen 3.2s ease-in-out 1.2s infinite;
        }
        .p-button.p-button-text.p-button-plain:not(:disabled):hover {
            background: none;
            color: #6b7280;
            box-shadow: none;
        }

        .stn-close {
          position: absolute;
          top: 8px; right: 8px;
          width: 28px; height: 28px;
          border: none; background: transparent; cursor: pointer;
          border-radius: 999px; color: #e8dedeff;
        }

        /* hidden state for mount/unmount smoothness */
        .stn-hidden {
          opacity: 0;
          transform: translate(-12px, 20px) scale(.96);
          transition: opacity 220ms ease, transform 220ms ease;
          pointer-events: none;
        }
      `}</style>

      <div className="stn-wrap" dir={dir} style={{ textAlign: align }}>
        {/* Confetti behind the card */}
        {visible && !dismissed && (
          <div className="stn-confetti-layer" aria-hidden="true">
            {hats.map((h) => (
              <div
                key={h.id}
                className="stn-hat"
                style={{
                  left: `${h.left}%`,
                  fontSize: `${h.size}px`,
                  animationDuration: `${h.duration}s`,
                  animationDelay: `${h.delay}s`,
                  '--rot': `${h.rotate}deg`,
                  '--drift': `${h.drift}px`,
                }}
              >
                🎓
              </div>
            ))}
          </div>
        )}

        {/* Card */}
        <div
          className={`stn-card ${visible ? '' : 'stn-hidden'}`}
          role="dialog"
          aria-live="polite"
        >
          <div className="stn-grid">
            <div className="stn-icon-3d" aria-hidden="true">
              <div className="stn-sphere">
                <div className="stn-glow" />
                <div className="stn-inner">
                  <img
                    style={{ height: '60px' }}
                    src="images/gradicon.png"
                    alt="Graduate"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="stn-title">
                {isAr ? arabicTitle : englishTitle}
              </div>

              <div className="stn-text">
                {bodyLines.map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < bodyLines.length - 1 && <br />}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="stn-actions">
            <div className="stn-cta">
              <Button
                label={ctaLabel || (isAr ? 'ابدأ الاختبار' : 'Start the Test')}
                style={{ height: '35px', width: 'max-content' }}
                className="p-button-rounded"
                onClick={onStart}
              />
            </div>

            <Button
              label={isAr ? 'تجاهل' : 'Dismiss'}
              style={{ fontWeight: '200' }}
              className="p-button-text p-button-plain"
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default StartTestNudge;
